// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { type ShopConfigurationId, type TaskType, type TaskTypeGroup, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Button, Card, Checkbox, Flex, Group, ScrollArea, Stack, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { WrappedCheckbox } from 'components/Mantine/WrappedCheckbox';
import { useGetAllDataFromFetcher } from 'helpers/getAllDataFromFetcher';
import { useTaskTypeModals } from 'modules/Shop/Tasks/TaskTypes/modalRegistries';
import { TaskTypeList } from 'modules/Shop/Tasks/TaskTypes/TaskTypeList';
import type { TaskGroupFilter } from 'modules/Shop/Tasks/TaskTypes/types';
import type { SelectedTasksAction } from 'modules/Shop/Tasks/TaskTypes/useSelectedTasks';

const EmptyTasksState = ({ onTaskTypeCreated }: { onTaskTypeCreated: () => void }) => {
  const { openModal } = useTaskTypeModals();
  return (
    <Flex flex={1} align="center" justify="center">
      <Card withBorder radius="md" p="xl" maw={420}>
        <Stack align="center" gap="sm">
          <Text fw={600} fz="lg">
            Tasks
          </Text>
          <Text c="dimmed" ta="center" fz="sm">
            Create your first task now. Add to a task group for optimal organization.
          </Text>
          <Button
            leftSection={<EvolveIcon icon="Add" size="xs" />}
            onClick={() => openModal('CreateEditTaskTypeModal', { onTaskTypeCreated })}
          >
            New Task
          </Button>
        </Stack>
      </Card>
    </Flex>
  );
};

type TaskGroupTaskListProps = {
  selectedTasks: TaskType[];
  updateSelectedTasks: (action: SelectedTasksAction) => void;
  groupFilter: TaskGroupFilter;
  /** Used to fill out `isAssignedToWorkCell` on each task type. */
  shopConfigurationId: ShopConfigurationId | undefined;
  onTaskTypesChange: () => void;
};

type SharedListProps = Pick<
  TaskGroupTaskListProps,
  'selectedTasks' | 'updateSelectedTasks' | 'shopConfigurationId' | 'onTaskTypesChange'
>;

type TaskListLayoutProps = {
  taskSearch: string;
  onTaskSearchChange: (taskSearch: string) => void;
  selectCheckbox: ReactNode;
  /** Pre-fills the group when creating a new task type. */
  defaultTaskTypeGroup?: TaskTypeGroup;
  onTaskTypeCreated: () => void;
  /** The task list rendered in the scrollable body. */
  children: ReactNode;
};

const TaskListLayout = ({
  taskSearch,
  onTaskSearchChange,
  selectCheckbox,
  defaultTaskTypeGroup,
  onTaskTypeCreated,
  children,
}: TaskListLayoutProps) => {
  const { openModal } = useTaskTypeModals();
  return (
    <Stack flex={1} p="md" gap="sm" style={{ overflow: 'hidden' }}>
      <Group gap="sm" align="center">
        {selectCheckbox}

        <TextInputDebounced
          flex={1}
          placeholder="Search tasks"
          leftSection={<EvolveIcon icon="Search" />}
          value={taskSearch}
          onChange={onTaskSearchChange}
        />

        <Button
          variant="light"
          leftSection={<EvolveIcon icon="Add" size="xs" />}
          onClick={() => openModal('CreateEditTaskTypeModal', { defaultTaskTypeGroup, onTaskTypeCreated })}
        >
          New task
        </Button>
      </Group>

      <ScrollArea flex={1}>{children}</ScrollArea>
    </Stack>
  );
};

/** Loads every task type in the group up front so select-all can act on the whole group. */
const SpecificGroupTaskList = ({
  taskTypeGroup,
  selectedTasks,
  updateSelectedTasks,
  shopConfigurationId,
  onTaskTypesChange,
}: SharedListProps & { taskTypeGroup: TaskTypeGroup }) => {
  const [taskSearch, setTaskSearch] = useState('');
  const { fetchPage } = useEvolvePaginatedGet('shop/taskType');

  const fetchConfig = useMemo(
    () => ({
      params: {
        taskTypeGroupId: taskTypeGroup.taskTypeGroupId,
        taskTypeName: taskSearch ? encodeURIComponent(taskSearch) : undefined,
        shopConfigurationId,
      },
    }),
    [taskTypeGroup.taskTypeGroupId, taskSearch, shopConfigurationId],
  );
  const [loadedTasks, loading, { setConfig, refetch }] = useGetAllDataFromFetcher(fetchPage, fetchConfig);
  useEffect(() => setConfig(fetchConfig), [fetchConfig, setConfig]);

  const selectableTaskTypes = useMemo(
    () => loadedTasks.filter((taskType) => !taskType.isAssignedToWorkCell),
    [loadedTasks],
  );
  const areAllSelected = useMemo(() => {
    const selectedTaskTypeIds = new Set(selectedTasks.map((selected) => selected.taskTypeId));
    return (
      selectableTaskTypes.length > 0 &&
      selectableTaskTypes.every((taskType) => selectedTaskTypeIds.has(taskType.taskTypeId))
    );
  }, [selectableTaskTypes, selectedTasks]);

  const handleTaskTypesChanged = () => {
    refetch();
    onTaskTypesChange();
  };

  return (
    <TaskListLayout
      taskSearch={taskSearch}
      onTaskSearchChange={setTaskSearch}
      selectCheckbox={
        <WrappedCheckbox
          label={loading ? 'Select all' : `Select (${selectableTaskTypes.length})`}
          loading={loading}
          value={areAllSelected}
          disabled={selectableTaskTypes.length === 0}
          onChange={(checked) =>
            updateSelectedTasks(
              checked
                ? { type: 'selectAll', taskTypes: selectableTaskTypes }
                : { type: 'deselectAll', taskTypes: loadedTasks },
            )
          }
        />
      }
      defaultTaskTypeGroup={taskTypeGroup}
      onTaskTypeCreated={handleTaskTypesChanged}
    >
      <TaskTypeList
        loading={loading}
        groupTasks={loadedTasks}
        entireCount={loadedTasks.length}
        nothingFoundText={taskSearch ? 'No tasks match your search.' : 'No tasks in this group.'}
        selectedTasks={selectedTasks}
        updateSelectedTasks={updateSelectedTasks}
        onTaskTypesChanged={handleTaskTypesChanged}
      />
    </TaskListLayout>
  );
};

/** Fetches one page at a time as the user scrolls; select-all is unavailable since the full list isn't loaded. */
const PaginatedTaskList = ({
  groupFilter,
  selectedTasks,
  updateSelectedTasks,
  shopConfigurationId,
  onTaskTypesChange,
}: SharedListProps & { groupFilter: 'all' | 'ungrouped' }) => {
  const [taskSearch, setTaskSearch] = useState('');

  const defaultOpts = useMemo(
    () => ({
      defaultConfig: {
        params: {
          hasNoTaskTypeGroup: groupFilter === 'ungrouped' ? true : undefined,
          taskTypeName: taskSearch ? encodeURIComponent(taskSearch) : undefined,
          shopConfigurationId,
        },
      },
    }),
    [groupFilter, taskSearch, shopConfigurationId],
  );
  const {
    data: tasks,
    entireCount,
    loading,
    fetchNextPage,
    reset,
    setDefaultOpts,
  } = useEvolvePaginatedGet('shop/taskType', defaultOpts);
  useEffect(() => setDefaultOpts(defaultOpts), [defaultOpts, setDefaultOpts]);

  const handleTaskTypesChanged = () => {
    reset();
    onTaskTypesChange();
  };

  if (groupFilter === 'all' && !taskSearch && entireCount === 0) {
    return <EmptyTasksState onTaskTypeCreated={handleTaskTypesChanged} />;
  }

  return (
    <TaskListLayout
      taskSearch={taskSearch}
      onTaskSearchChange={setTaskSearch}
      selectCheckbox={<Checkbox label="Select all" disabled />}
      onTaskTypeCreated={handleTaskTypesChanged}
    >
      <TaskTypeList
        loading={loading}
        groupTasks={tasks}
        entireCount={entireCount}
        nothingFoundText={taskSearch ? 'No tasks match your search.' : 'No tasks in this group.'}
        fetchNextPage={fetchNextPage}
        selectedTasks={selectedTasks}
        updateSelectedTasks={updateSelectedTasks}
        onTaskTypesChanged={handleTaskTypesChanged}
      />
    </TaskListLayout>
  );
};

export const TaskGroupTaskList = ({ groupFilter, ...props }: TaskGroupTaskListProps) =>
  typeof groupFilter === 'object' ? (
    <SpecificGroupTaskList key={groupFilter.taskTypeGroupId} taskTypeGroup={groupFilter} {...props} />
  ) : (
    <PaginatedTaskList key={groupFilter} groupFilter={groupFilter} {...props} />
  );
